import { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { SOCIALS } from "@/data/mock";

const defaults = {
  site_name: "GRUPO MANGA",
  tagline: "SINAIS • ANÁLISES • PLATAFORMAS OFICIAIS",
  logo: "/manga-symbol.png",
  instagram: SOCIALS.instagram,
  telegram: SOCIALS.telegram,
  whatsapp: "",
  email: SOCIALS.email,
  copyright: "© 2026 Grupo Manga. Todos os direitos reservados.",
  cnpj: "",
};

const SETTINGS_UPDATED_EVENT = "grupomanga:settings-updated";
const POLL_INTERVAL_MS = 10000; // 10s polling

const Ctx = createContext({ ...defaults, refresh: () => {} });

export const SiteConfigProvider = ({ children }) => {
  const [cfg, setCfg] = useState(defaults);
  const versionRef = useRef(""); // JSON string of last payload to skip re-render if unchanged

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/public/settings");
      const merged = { ...defaults, ...data };
      const stamp = JSON.stringify(merged);
      if (stamp !== versionRef.current) {
        versionRef.current = stamp;
        setCfg(merged);
      }
    } catch {
      /* silent */
    }
  }, []);

  // Initial fetch + interval polling
  useEffect(() => {
    load();
    const id = setInterval(load, POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [load]);

  // Refetch on window focus / tab visibility change
  useEffect(() => {
    const onFocus = () => load();
    const onVisibility = () => {
      if (document.visibilityState === "visible") load();
    };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [load]);

  // Cross-tab + intra-app broadcast trigger
  useEffect(() => {
    const onCustom = () => load();
    const onStorage = (e) => {
      if (e.key === SETTINGS_UPDATED_EVENT) load();
    };
    window.addEventListener(SETTINGS_UPDATED_EVENT, onCustom);
    window.addEventListener("storage", onStorage);
    return () => {
      window.removeEventListener(SETTINGS_UPDATED_EVENT, onCustom);
      window.removeEventListener("storage", onStorage);
    };
  }, [load]);

  const value = { ...cfg, refresh: load };
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
};

// Helper for admin to trigger immediate refresh across app + other tabs
export const broadcastSettingsUpdate = () => {
  try {
    window.dispatchEvent(new Event(SETTINGS_UPDATED_EVENT));
    localStorage.setItem(SETTINGS_UPDATED_EVENT, String(Date.now()));
  } catch {
    /* noop */
  }
};

export const useSiteConfig = () => useContext(Ctx);
